import { Button, createListCollection, Text, VStack } from '@chakra-ui/react';
import { SensorOrSubSensorInfoByType } from '@components/cockpit/panels/monitoring/monitoring.types';
import PopupContainer from '@components/map/popup/common/PopupContainer';
import { CustomSelect } from '@components/ui/select/Select';
import { ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveSite, selectActiveUserLogin } from '@redux/authent/authent.selectors';
import { selectNeutralInfo } from '@redux/situation/situation.selectors';
import { usePostMajesCommandMutation } from '@services/c2/c2.api';
import {
  getSectorConfigurations,
  getSectorId,
  getSectorName,
  getSelectedSectorConfiguration,
} from '@utils/sensors/neutral/neutral.utils';
import { createSensorToast } from '@utils/sensors/sensors.utils';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { MajesSetSectorModeCommand, Mc2CommandEnum } from '@/types/c2/c2.types';
import { MonitoringSensorByType } from '@/types/sensor/monitoring.types';
import { SensorTypeEnum } from '@/types/sensor/sensors';

type Props = {
  sensorOrSubSensorInfo: SensorOrSubSensorInfoByType<SensorTypeEnum.DFB6>;
  inMonitoring: boolean;
};

export default function Dfb6Commands({ sensorOrSubSensorInfo, inMonitoring }: Readonly<Props>) {
  const { configuration, status, type, active } = sensorOrSubSensorInfo;
  const [postMajesCommand] = usePostMajesCommandMutation();
  const activeSite = useAppSelector(selectActiveSite);
  const selectedModeConfiguration = status
    ? getSelectedSectorConfiguration({
        status,
        configuration,
        type,
        active: true,
      })
    : null;
  const [selectedMode, setSelectedMode] = useState<string | undefined>(
    selectedModeConfiguration ? getSectorId(selectedModeConfiguration).toString() : undefined,
  );
  const [prevSelectedModeConfiguration, setPrevSelectedModeConfiguration] = useState(selectedModeConfiguration);
  const [isLoadingChangeSelectedMode, setIsLoadingChangeSelectedMode] = useState<boolean>(false);
  const activeUserLogin = useAppSelector(selectActiveUserLogin);
  const jammerLogin = useAppSelector((state) => selectNeutralInfo(state, false))?.login ?? null;
  const isNonActiveUserJamming = jammerLogin !== null && activeUserLogin !== jammerLogin;

  if (status === null && !inMonitoring) {
    return null;
  }

  const sectorConfigurations = getSectorConfigurations({
    status,
    configuration,
    type,
    active,
  } as MonitoringSensorByType<SensorTypeEnum.DFB6>);

  function handleChangeSelectedSector() {
    const sector = sectorConfigurations.find((sector) => getSectorId(sector).toString() === selectedMode);
    if (sector && !isNonActiveUserJamming) {
      setIsLoadingChangeSelectedMode(true);
      const toastParams = {
        command: 'sensors.commands.dfb6.changeModeCommand',
        name: configuration.name,
      };
      createSensorToast({
        ...toastParams,
        toast: ToastStatusEnum.INFO,
      });
      postMajesCommand({
        appCode: configuration.appCode,
        site: activeSite?.code ?? '',
        command: {
          type: Mc2CommandEnum.SetSectorMode,
          sensorCode: configuration.code,
          sectorMode: getSectorId(sector),
        } as MajesSetSectorModeCommand,
      })
        .unwrap()
        .then(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.SUCCESS,
          }),
        )
        .catch(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.ERROR,
          }),
        );
    }
  }

  if (prevSelectedModeConfiguration?.modeId !== selectedModeConfiguration?.modeId) {
    setPrevSelectedModeConfiguration(selectedModeConfiguration);
    setSelectedMode(selectedModeConfiguration ? getSectorId(selectedModeConfiguration).toString() : undefined);
  }

  if (
    isLoadingChangeSelectedMode &&
    selectedModeConfiguration &&
    getSectorId(selectedModeConfiguration).toString() === selectedMode
  ) {
    setIsLoadingChangeSelectedMode(false);
  }

  const sectors = createListCollection({
    items: sectorConfigurations
      .filter((sector) => sector.active)
      .map((sector) => ({
        label: getSectorName(sector),
        value: getSectorId(sector).toString(),
      })),
  });

  return (
    <PopupContainer height="100%">
      <VStack gap="standardS" width="100%">
        <Text textStyle="md" fontWeight="medium">
          <FormattedMessage id="sensors.tab.commands.dfb6.changeSelectedMode" />
        </Text>
        <CustomSelect
          variant="darker"
          size="sm"
          key={selectedMode}
          value={selectedMode}
          collection={sectors}
          disabled={isLoadingChangeSelectedMode || isNonActiveUserJamming || !status}
          onChange={(value) => setSelectedMode(value ?? undefined)}
        />
        <Button
          disabled={
            !status ||
            (!!selectedModeConfiguration && getSectorId(selectedModeConfiguration).toString() === selectedMode) ||
            isNonActiveUserJamming
          }
          loading={isLoadingChangeSelectedMode}
          size={{ base: 'sm', '4k': 'md' }}
          onClick={handleChangeSelectedSector}
        >
          <FormattedMessage id="global.validate" />
        </Button>
      </VStack>
    </PopupContainer>
  );
}
